/**
 * DeviceTypePie — donut chart for the Dashboard's "Device Type
 * Distribution" panel.
 *
 * Design rules:
 *   - No chart library — keep the dep surface tight.
 *   - Pie tops out at TOP_N slices; anything beyond gets bucketed into
 *     a gray "Other" slice. The legend below still lists every type so
 *     no information is lost — only visual noise is bounded.
 *   - Donut (not pie) so the centre can carry the total + hovered count.
 *   - Each slice has a 2 px stroke in the panel background colour so
 *     they read as distinct shapes even at narrow sweep angles.
 *   - Light + dark mode driven by CSS variables; no hard-coded colours
 *     in JS beyond what the caller passes in.
 */

import React, { useMemo, useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';

interface Slice {
  type: string;
  label: string;
  count: number;
  color: string;
}

interface DeviceTypePieProps {
  slices: Slice[];
  total: number;
  /** Outer diameter in px. Defaults to a responsive value via CSS. */
  size?: number;
  /** Max number of real slices before the rest collapse into "Other". */
  topN?: number;
}

const DEFAULT_TOP_N = 7;
const OTHER_COLOR = '#9ca3af'; // theme-neutral gray; overridden in dark mode via CSS

// ─── Geometry helpers ────────────────────────────────────────────────
function polar(cx: number, cy: number, r: number, deg: number) {
  const rad = (deg - 90) * (Math.PI / 180);
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

/** Build an annulus-segment path (donut slice). Handles the 360° case. */
function donutPath(cx: number, cy: number, rOuter: number, rInner: number, startDeg: number, endDeg: number): string {
  const sweep = endDeg - startDeg;
  if (sweep >= 360) {
    // Full donut as two stitched arcs at each radius.
    return [
      `M ${cx} ${cy - rOuter}`,
      `A ${rOuter} ${rOuter} 0 1 1 ${cx} ${cy + rOuter}`,
      `A ${rOuter} ${rOuter} 0 1 1 ${cx} ${cy - rOuter}`,
      `Z`,
      `M ${cx} ${cy - rInner}`,
      `A ${rInner} ${rInner} 0 1 0 ${cx} ${cy + rInner}`,
      `A ${rInner} ${rInner} 0 1 0 ${cx} ${cy - rInner}`,
      `Z`,
    ].join(' ');
  }
  const oStart = polar(cx, cy, rOuter, startDeg);
  const oEnd = polar(cx, cy, rOuter, endDeg);
  const iEnd = polar(cx, cy, rInner, endDeg);
  const iStart = polar(cx, cy, rInner, startDeg);
  const largeArc = sweep > 180 ? 1 : 0;
  return [
    `M ${oStart.x} ${oStart.y}`,
    `A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${oEnd.x} ${oEnd.y}`,
    `L ${iEnd.x} ${iEnd.y}`,
    `A ${rInner} ${rInner} 0 ${largeArc} 0 ${iStart.x} ${iStart.y}`,
    `Z`,
  ].join(' ');
}

interface Arc extends Slice {
  startDeg: number;
  endDeg: number;
  midDeg: number;
  /** Aggregated children when this arc is the "Other" bucket. */
  children?: Slice[];
}

export const DeviceTypePie: React.FC<DeviceTypePieProps> = ({ slices, total, size = 260, topN = DEFAULT_TOP_N }) => {
  const { t } = useI18n();
  const [hover, setHover] = useState<string | null>(null);

  // 1) Sort by count desc and discard zero-count entries — they confuse
  //    the hover targets (degenerate arcs) and crowd the legend.
  const ranked = useMemo(() =>
    [...slices].filter(s => s.count > 0).sort((a, b) => b.count - a.count),
    [slices]
  );

  // 2) Compose the arc list: at most `topN` real slices + one "Other"
  //    bucket if more types exist beyond that.
  const arcs: Arc[] = useMemo(() => {
    if (total === 0 || ranked.length === 0) return [];
    const head = ranked.slice(0, topN);
    const tail = ranked.slice(topN);
    const segments: Slice[] = [...head];
    if (tail.length > 0) {
      const otherCount = tail.reduce((s, x) => s + x.count, 0);
      segments.push({
        type: '__other__',
        label: t('dashboard.otherTypes', { count: tail.length.toString() }),
        count: otherCount,
        color: OTHER_COLOR,
      });
    }
    let cursor = 0;
    return segments.map(s => {
      const sweep = (s.count / total) * 360;
      const arc: Arc = {
        ...s,
        startDeg: cursor,
        endDeg: cursor + sweep,
        midDeg: cursor + sweep / 2,
        children: s.type === '__other__' ? tail : undefined,
      };
      cursor += sweep;
      return arc;
    });
  }, [ranked, total, topN, t]);

  if (arcs.length === 0) {
    return <div className="empty-state">{t('dashboard.noDevicesToDisplay')}</div>;
  }

  // 3) Layout constants — donut + a small radial nudge on hover.
  const cx = size / 2;
  const cy = size / 2;
  const rOuter = size / 2 - 6;
  const rInner = rOuter * 0.58;
  const HOVER_NUDGE = 6;

  const hovered = arcs.find(a => a.type === hover);

  // 4) Tooltip strings — multi-line for the "Other" bucket so hovering it
  //    shows what's in it without forcing the user to scroll the legend.
  const tooltipFor = (a: Arc) => {
    const pct = Math.round((a.count / total) * 100);
    const head = `${a.label}: ${a.count.toLocaleString()} (${pct}%)`;
    if (!a.children || a.children.length === 0) return head;
    const lines = a.children
      .map(c => `  ${c.label}: ${c.count.toLocaleString()}`)
      .join('\n');
    return `${head}\n${lines}`;
  };

  return (
    <div className="device-type-pie">
      <div className="device-type-pie-svg-wrap">
        <svg
          viewBox={`0 0 ${size} ${size}`}
          role="img"
          aria-label={t('dashboard.deviceTypeDistribution')}
        >
          {arcs.map(a => {
            const nudged = hover === a.type
              ? polar(0, 0, HOVER_NUDGE, a.midDeg)
              : { x: 0, y: 0 };
            return (
              <g
                key={a.type}
                transform={`translate(${nudged.x} ${nudged.y})`}
                className="device-type-pie-slice-g"
              >
                <path
                  d={donutPath(cx, cy, rOuter, rInner, a.startDeg, a.endDeg)}
                  fill={a.color}
                  className={`device-type-pie-slice${hover === a.type ? ' is-hover' : ''}`}
                  onMouseEnter={() => setHover(a.type)}
                  onMouseLeave={() => setHover(null)}
                  fillRule="evenodd"
                >
                  <title>{tooltipFor(a)}</title>
                </path>
              </g>
            );
          })}

          {/* Centre — total devices, or the hovered slice's name/count. */}
          <text x={cx} y={cy - 6} textAnchor="middle" className="device-type-pie-centre-value">
            {(hovered ? hovered.count : total).toLocaleString()}
          </text>
          <text x={cx} y={cy + 14} textAnchor="middle" className="device-type-pie-centre-label">
            {hovered ? hovered.label : t('dashboard.totalDevices')}
          </text>
        </svg>
      </div>

      {/* Legend lists EVERY ranked type — even the ones that got bucketed
          into "Other" — so no information disappears at scale. The list
          scrolls when there are more rows than fit. */}
      <ul className="device-type-pie-legend">
        {ranked.map((s, i) => {
          const bucketed = i >= topN;
          const pct = total > 0 ? Math.round((s.count / total) * 100) : 0;
          const hoverKey = bucketed ? '__other__' : s.type;
          return (
            <li
              key={s.type}
              className={[
                'device-type-pie-legend-item',
                hover === hoverKey ? 'is-hover' : '',
                bucketed ? 'is-bucketed' : '',
              ].filter(Boolean).join(' ')}
              onMouseEnter={() => setHover(hoverKey)}
              onMouseLeave={() => setHover(null)}
              title={bucketed ? t('dashboard.bucketedInOther') : undefined}
            >
              <span
                className="device-type-pie-legend-dot"
                style={{ backgroundColor: bucketed ? OTHER_COLOR : s.color }}
              />
              <span className="device-type-pie-legend-label">{s.label}</span>
              <span className="device-type-pie-legend-count">{s.count.toLocaleString()}</span>
              <span className="device-type-pie-legend-pct">{pct}%</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
